/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Grid, InputAdornment, IconButton, Autocomplete, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { Assistant } from '@mui/icons-material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { Formik } from 'formik';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useDispatch, useSelector } from 'react-redux';
import ActionButton from 'modules/common/components/action-button';
import { TextField } from 'modules/common/components/add-hotel-group/style';
import { dashboardActions } from 'modules/dashboard/slice';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import {
  selectHotelSetQuery,
  selectLoader,
  selectNaturalLanguageQueryList,
} from 'modules/dashboard/selectors';
import PAGINATION from 'modules/common/constants/pagination-limits';
import ORDER_BY from 'modules/common/constants/order-by';
import llmInputBoxValidation from '../../validation/llm-input-validation';

/** Implement a natural language input box, and based on the input will generate LLM output
 * @param {Boolean} open - To open/ close dialog box
 * @param {Boolean} isFirstTime - To enable/disable advanced querying feature
 * @returns
 */

const LLMInputBox = ({ open, isFirstTime, setIsFocus, setFilterError }) => {
  const dispatch = useDispatch();
  //
  const naturalLanguageText = useSelector(selectHotelSetQuery);
  const naturalLanguageQueryList = useSelector(selectNaturalLanguageQueryList);
  const authUser = useSelector(selectAuthUser);
  //
  const organizationId = useSelector(selectOrganizationId);
  const loading = useSelector(selectLoader);

  // To preview values for Group A and B based on the input query
  const generateValues = (payload) => {
    dispatch(
      dashboardActions.getAdvancedQueryLLM({
        organizationId,
        userId: authUser?.id,
        ...payload,
      })
    );
  };
  //
  useEffect(() => {
    const query = `limit=${PAGINATION.NATURAL_LANGUAGE_QUERY_LIST_PER_PAGE}&sortBy=createdAt:${ORDER_BY.DESCENDING}`;
    dispatch(
      dashboardActions.getNaturalLanguageQuery({
        organizationId,
        userId: authUser?.id,
        query,
      })
    );
  }, []);
  //
  return (
    open && (
      <Formik
        initialValues={{ naturalLanguageText }}
        validationSchema={llmInputBoxValidation}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={generateValues}
        enableReinitialize
      >
        {({ values, errors, handleBlur, handleSubmit, setFieldValue, touched, resetForm }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Autocomplete
              disableClearable
              freeSolo
              options={naturalLanguageQueryList}
              value={values.naturalLanguageText || ''}
              onOpen={() => setIsFocus(true)}
              onChange={(_, newValue) => {
                const value = typeof newValue === 'string' ? newValue : newValue?.label || '';
                setFieldValue('naturalLanguageText', value);
                dispatch(dashboardActions.setNaturalLanguageQuery(value));
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setFieldValue('naturalLanguageText', newInputValue);
                  dispatch(dashboardActions.setNaturalLanguageQuery(newInputValue));
                }
              }}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li {...otherProps} key={option.id}>
                    {option.label}
                  </li>
                );
              }}
              disabled={loading}
              // Optional: improve filtering if options are objects
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.label || '')}
              isOptionEqualToValue={(option, value) =>
                typeof value === 'string' ? option.label === value : option?.id === value?.id
              }
              // Allow pressing Enter to submit (when menu closed)
              handleHomeEndKeys
              selectOnFocus
              clearOnBlur={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="naturalLanguageText"
                  label="Natural Language Text"
                  size="small"
                  multiline
                  onBlur={handleBlur}
                  error={Boolean(touched.naturalLanguageText && errors.naturalLanguageText)}
                  helperText={touched.naturalLanguageText && errors.naturalLanguageText}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': { padding: 2, height: 'fit-content' },
                    '& .MuiInputAdornment-root .MuiIconButton-root': {
                      color: 'text.disabled',
                      '&:hover': { color: 'text.secondary' },
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      bgcolor: 'background.paper',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip
                          title="This feature uses generative AI to interpret your input and generate results."
                          arrow
                        >
                          <Assistant color="error" sx={{ cursor: 'pointer', fontSize: 20 }} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="Clear text"
                            size="small"
                            edge="end"
                            disabled={loading}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              resetForm();
                              dispatch(dashboardActions.setHotelGroupQuery(''));
                              dispatch(dashboardActions.setRestLLMRequestState(false));
                              dispatch(dashboardActions.setNaturalLanguageQuery(''));
                              setFilterError(false);
                            }}
                          >
                            <ClearRoundedIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      </>
                    ),
                  }}
                />
              )}
            />

            <Grid container direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Grid item>
                <ActionButton type="submit" label="Generate" disabled={loading || isFirstTime} />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    )
  );
};
//
export default LLMInputBox;
